import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownIcon } from "@heroicons/react/24/outline";
import ModernSupplierSelector from "../Selector/ModernSupplierSelector";
import { TWclassNames } from "../Div";
import { StringLimit } from "../../Utils";

export const ModernSupplierSelectorField = ({
  name = "supplier_id",
  label,
  selected,
  setFieldValue,
  errors,
  disabled = false,
  onChange,
  showField = true,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(selected || null);

  useEffect(() => {
    setSelectedSupplier(selected || null);
  }, [selected]);

  const handleChange = (suppliers) => {
    if (suppliers.length > 0) {
      const s = suppliers[0];
      setSelectedSupplier(s);
      const id = s.id ?? null;
      setFieldValue?.(name, id);
      onChange?.({
        target: { name, value: id, object: s },
      });
      setOpen(false);
    } else {
      setSelectedSupplier(null);
      setFieldValue?.(name, null);
      onChange?.({
        target: { name, value: null, object: null },
      });
      setOpen(false);
    }
  };

  const displayText = selectedSupplier
    ? selectedSupplier.displayTitle || selectedSupplier.name
    : null;

  if (!showField) return null;

  return (
    <>
      <div>
        {label && (
          <label htmlFor={name} className="block text-sm font-medium text-gray-700">
            {t(label)}
          </label>
        )}
        <div className="mt-1 focus-within:border-indigo-600">
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                setOpen(true);
              }
            }}
            onClick={() => !disabled && setOpen(true)}
            className={TWclassNames(
              disabled
                ? "text-gray-400 cursor-not-allowed"
                : "cursor-pointer border-2 border-blue-400 hover:border-l-indigo-500",
              "relative py-1.5 px-1 bg-gray-50 shadow flex items-center rounded-md"
            )}
          >
            <span className="ml-2 flex">
              {displayText ? (
                <StringLimit text={displayText} limit={28} />
              ) : (
                t("tool.supplier.placeholder")
              )}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
              <ArrowDownIcon className="h-3 w-3" aria-hidden="true" />
            </span>
          </div>
        </div>
      </div>

      {errors && errors[name] && (
        <div className="flex mt-1 text-red-700 text-xs">
          <span className="ml-2">{errors[name]}</span>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
            <div
              className="relative transform overflow-hidden rounded-none bg-white text-left shadow-xl transition-all sm:rounded-lg sm:my-8 w-full sm:max-w-2xl h-[80vh] sm:h-auto"
            >
              <ModernSupplierSelector
                open={open}
                selectedSuppliers={selectedSupplier ? [selectedSupplier] : []}
                onChange={handleChange}
                onClose={() => setOpen(false)}
                multiple={false}
                showHeader
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
